import type {
  ICreateShipment,
  SimulatePostageEndDate,
  SimulatePostagePrice,
} from '@interfaces/correios';
import { correios, coleta } from './api';
import { popupError } from './Zendesk';
import type { Dict } from 'styled-components/dist/types';

// TODO: COLOCAR TOKEN NO INTERCEPTOR
export const createToken = async (cardNumber: string) => {
  const body = { numero: cardNumber };
  try {
    const response = await correios.post('token/v1/autentica/cartaopostagem', body);
    return response;
  } catch (error) {
    console.error('Error to create token:', error);
    throw error;
  }
};

export const createColetaToken = async () => {
  try {
    const response = await correios.post('token/v1/autentica');
    return response;
  } catch (error) {
    popupError(`Erro ao criar token Coleta:${error}`, true);
    throw error;
  }
};

interface ICorreiosError {
  msgs?: string[];
  date?: string;
  path?: string;
  causa?: string;
}

function parseCorreiosError(error: any): string[] {
  try {
    const data: ICorreiosError | undefined = error?.response?.data;

    if (data?.msgs && Array.isArray(data.msgs)) {
      return data.msgs;
    }

    if (data) {
      return [`Erro Correios: ${data.causa || JSON.stringify(data)}`];
    }

    // fallback geral
    return ['Erro desconhecido ao chamar Correios'];
  } catch (e) {
    return ['Erro inesperado ao processar resposta da API Correios'];
  }
}

export const createPostage = async (token: string, body: ICreateShipment) => {
  try {
    const response = await correios.post('prepostagem/v1/prepostagens', body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 403) {
      throw error;
    }
    const erros = parseCorreiosError(error);
    popupError(`Erro ao criar postagem:${erros}`, true);
    throw new Error(erros.join(' | '));
  }
};

export const parseColeta = (xml: string) => {
  let response: Dict = {};
  const parser = new DOMParser();
  const doc: XMLDocument = parser.parseFromString(xml, 'text/xml');
  response['cod_erro'] = doc.getElementsByTagName('cod_erro')[0]?.textContent;
  response['codigo_erro'] = doc.getElementsByTagName('codigo_erro')[0]?.textContent;
  response['msg_erro'] = doc.getElementsByTagName('msg_erro')[0]?.textContent;
  response['descricao_erro'] = doc.getElementsByTagName('descricao_erro')[0]?.textContent;
  response['numero_coleta'] = doc.getElementsByTagName('numero_coleta')[0]?.textContent;
  response['numero_etiqueta'] = doc.getElementsByTagName('numero_etiqueta')[0]?.textContent;
  response['prazo'] = doc.getElementsByTagName('prazo')[0]?.textContent;
  response['data_solicitacao'] = doc.getElementsByTagName('data_solicitacao')[0]?.textContent;
  return response;
};

export const createColeta = async (soapEnvelope: string) => {
  try {
    const response = await coleta.post('/', soapEnvelope, {
      headers: {
        'Content-Type': 'text/xml',
      },
    });
    return response;
  } catch (error: any) {
    if (error?.response?.status === 403) throw error;
    const erros = parseCorreiosError(error);
    popupError(`Erro ao criar coleta:${erros}`, true);
    throw new Error(erros.join(' | '));
  }
};

const xmlEscape = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const generateColeta = (body: ICreateShipment, data: string) => {
  const soapEnvelope = `
  <soapenv:Envelope
  	xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
  	xmlns:ser="http://service.logisticareversa.correios.com.br/">
  	<soapenv:Header/>
  	<soapenv:Body>
  		<ser:solicitarPostagemReversa>
  			<codAdministrativo>11189010</codAdministrativo>
  			<codigo_servico>${xmlEscape(body.codigoServico)}</codigo_servico>
  			<cartao>0078663750</cartao>
  			<destinatario>
  				<nome>${xmlEscape(body.destinatario.nome)}</nome>
  				<logradouro>${xmlEscape(body.destinatario.endereco.logradouro)}</logradouro>
  				<numero>${xmlEscape(body.destinatario.endereco.numero)}</numero>
  				<complemento>${xmlEscape(body.destinatario.endereco.complemento)}</complemento>
  				<bairro>${xmlEscape(body.destinatario.endereco.bairro)}</bairro>
  				<referencia>null</referencia>
  				<cidade>${xmlEscape(body.destinatario.endereco.cidade)}</cidade>
  				<uf>${xmlEscape(body.destinatario.endereco.uf)}</uf>
  				<cep>${xmlEscape(body.destinatario.endereco.cep)}</cep>
  				<ddd>${xmlEscape(body.destinatario.dddCelular)}</ddd>
  				<telefone>${xmlEscape(body.destinatario.celular)}</telefone>
  				<email>${xmlEscape(body.destinatario.email)}</email>
  			</destinatario>
  			<coletas_solicitadas>
  				<tipo>CA</tipo>
  				<id_cliente>${xmlEscape(body.remetente.nome)}_${xmlEscape(body.remetente.cpfCnpj)}_${Date.now()}</id_cliente>
  				<valor_declarado>${body.itensDeclaracaoConteudo.reduce((sum, item) => sum + parseFloat(item.valor), 0).toFixed(2)}</valor_declarado>
  				<descricao></descricao>
  				<ag>${xmlEscape(data)}</ag>
  				<remetente>
  					<nome>${xmlEscape(body.remetente.nome)}</nome>
  					<logradouro>${xmlEscape(body.remetente.endereco.logradouro)}</logradouro>
  					<numero>${xmlEscape(body.remetente.endereco.numero)}</numero>
  					<complemento>${xmlEscape(body.remetente.endereco.complemento)}</complemento>
  					<bairro>${xmlEscape(body.remetente.endereco.bairro)}</bairro>
  					<cidade>${xmlEscape(body.remetente.endereco.cidade)}</cidade>
  					<uf>${xmlEscape(body.remetente.endereco.uf)}</uf>
  					<cep>${xmlEscape(body.remetente.endereco.cep)}</cep>
  					<ddd>${xmlEscape(body.remetente.dddTelefone)}</ddd>
  					<telefone>${xmlEscape(body.remetente.telefone)}</telefone>
  					<email>${xmlEscape(body.remetente.email)}</email>
  					<identificacao>${xmlEscape(body.destinatario.cpfCnpj)}</identificacao>
  				</remetente>
  				<obj_col>
  					<id></id>
  					<item>${body.itensDeclaracaoConteudo.length}</item>
  					<desc>${xmlEscape(body.itensDeclaracaoConteudo[0].conteudo)}</desc>
  				</obj_col>
  			</coletas_solicitadas>
  		</ser:solicitarPostagemReversa>
  	</soapenv:Body>
  </soapenv:Envelope>
  `;
  return soapEnvelope;
};

export const getColeta = async (cartaoPostagem: string, token: string) => {
  try {
    const response = await correios.get('coletainterativa/v1/solicitacoes-coletas', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        numeroCartaoPostagem: cartaoPostagem,
        dataInicio: new Date().toISOString().split('T')[0],
        dataFim: new Date(new Date().getTime() + 30 * 86400000).toISOString().split('T')[0],
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get coleta:', error);
    throw error;
  }
};

export const getPostage = async (idPostage: string, token: string) => {
  try {
    const response = await correios.get(`srorastro/v1/objetos/${idPostage}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get postage:', error);
    throw error;
  }
};

export const simulatePostageEndDate = async (
  params: SimulatePostageEndDate,
  token: string,
  cardNumber: string
) => {
  try {
    const response = await correios.post('prazo/v1/nacional', params, {
      headers: {
        Authorization: `Bearer ${token}`,
        numeroCartaoPostagem: cardNumber,
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get tracking:', error);
    throw error;
  }
};

export const simulatePostagePrice = async (
  params: SimulatePostagePrice,
  token: string,
  cardNumber: string
) => {
  try {
    const response = await correios.post('preco/v1/nacional', params, {
      headers: {
        Authorization: `Bearer ${token}`,
        numeroCartaoPostagem: cardNumber,
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get tracking:', error);
    throw error;
  }
};

export const getCep = async (cep: string, token: string) => {
  try {
    const response = await correios.get(`cep/v2/enderecos/${cep}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get tracking:', error);
    throw error;
  }
};

export const getPrepostage = async (codigoObjeto: string, token: string) => {
  try {
    const response = await correios.get(`v1/prepostagens/postada`, {
      params: { codigoObjeto },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error to get tracking:', error);
    throw error;
  }
};

export async function withTokenRetry<T>(
  fn: (token: string) => Promise<T>,
  currentToken: string,
  area: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNewToken: (tokenData: any) => void
): Promise<T> {
  try {
    return await fn(currentToken);
  } catch (error: any) {
    if (error?.response?.status === 403) {
      const cardNumber = area === 'SAC E-COM' ? '0079515169' : '0079515150';
      const newTokenResponse = await createToken(cardNumber);
      if (newTokenResponse.status !== 201) {
        popupError('Erro ao renovar token dos Correios', true);
        throw new Error('Erro ao renovar token dos Correios');
      }
      onNewToken(newTokenResponse.data);
      return await fn(newTokenResponse.data.token);
    }
    throw error;
  }
}
